(() => {
  const FREE_GIFT_KEY = "_free_gift";
  const FREE_GIFT_PARENT_KEY = "_free_gift_parent";
  const STATE_KEY = "farmry_free_gift_cart_state";
  const MAP_SECTION_ID = "free-gift-map";
  let syncing = false;

  const cartUrl = () => window.themeVariables.routes.cartUrl;
  const isCartPage = () => window.themeVariables?.settings?.pageType === "cart";

  const getProperty = (item, key) => {
    if (!item || !item.properties) return "";
    return item.properties[key] || "";
  };

  const isFreeGift = (item) => String(getProperty(item, FREE_GIFT_KEY)).toLowerCase() === "true";

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY) || "{}");
    } catch (error) {
      return {};
    }
  };

  const writeState = (cart) => {
    const parents = {};
    const gifts = {};

    (cart.items || []).forEach((item) => {
      if (isFreeGift(item)) {
        gifts[item.key] = item.quantity;
        return;
      }
      const productId = String(item.product_id);
      parents[productId] = (parents[productId] || 0) + item.quantity;
    });

    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({ parents, gifts }));
    } catch (error) {
      // Ignore quota / private mode failures
    }
  };

  const getParentQuantityMap = (cart) => {
    const qtyByParentId = {};

    (cart.items || []).forEach((item) => {
      if (isFreeGift(item)) return;
      const productId = String(item.product_id);
      qtyByParentId[productId] = (qtyByParentId[productId] || 0) + item.quantity;
    });

    return qtyByParentId;
  };

  const findPaidSibling = (cart, variantId, updates) => {
    return (cart.items || []).find((item) => {
      if (item.variant_id !== variantId || isFreeGift(item)) return false;
      if (updates[item.key] === 0) return false;
      return true;
    });
  };

  const queuePaidExcess = (cart, updates, paidAdds, variantId, excess) => {
    const paidSibling = findPaidSibling(cart, variantId, updates);
    if (paidSibling) {
      const baseQty = updates[paidSibling.key] != null ? updates[paidSibling.key] : paidSibling.quantity;
      updates[paidSibling.key] = baseQty + excess;
      return;
    }

    const existingAdd = paidAdds.find((entry) => entry.id === variantId);
    if (existingAdd) {
      existingAdd.quantity += excess;
    } else {
      paidAdds.push({ id: variantId, quantity: excess });
    }
  };

  const parseGiftMapFromHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const node = doc.querySelector("#farmry-free-gift-map");
      if (!node) return {};
      return JSON.parse((node.textContent || "").trim() || "{}");
    } catch (error) {
      console.error("Free gift map parse failed:", error);
      return {};
    }
  };

  const fetchGiftMap = async () => {
    const response = await fetch(`${cartUrl()}?section_id=${MAP_SECTION_ID}`, {
      credentials: "same-origin"
    });
    if (!response.ok) throw new Error("Failed to fetch free gift map");
    const html = await response.text();
    return parseGiftMapFromHtml(html);
  };

  const hasFreeGiftLine = (cart, parentId, variantId) => {
    return (cart.items || []).some((item) => {
      if (!isFreeGift(item)) return false;
      if (Number(item.variant_id) !== Number(variantId)) return false;
      return String(getProperty(item, FREE_GIFT_PARENT_KEY)) === String(parentId);
    });
  };

  /**
   * Cart safeguard: if a machine product has custom.free_gift metafields
   * but the matching free-gift line is missing, queue it for add.
   */
  const planMissingGifts = (cart, giftMap = {}) => {
    const parentQtyMap = getParentQuantityMap(cart);
    const giftAdds = [];
    const queued = {};

    Object.keys(parentQtyMap).forEach((parentId) => {
      const parentQty = parentQtyMap[parentId] || 0;
      if (parentQty <= 0) return;

      const gifts = giftMap[parentId];
      if (!Array.isArray(gifts) || gifts.length === 0) return;

      gifts.forEach((gift) => {
        if (!gift || !gift.id) return;
        if (hasFreeGiftLine(cart, parentId, gift.id)) return;

        const queueKey = `${parentId}:${gift.id}`;
        if (queued[queueKey]) return;
        queued[queueKey] = true;

        giftAdds.push({
          id: Number(gift.id),
          quantity: parentQty,
          properties: {
            [FREE_GIFT_KEY]: "true",
            [FREE_GIFT_PARENT_KEY]: String(parentId)
          }
        });
      });
    });

    return giftAdds;
  };

  /**
   * Free-gift qty always follows machine qty.
   * - Machine +/− → free-gift qty matches machine
   * - Accessory + alone → free qty stays = machine; excess becomes paid line
   * - Machine removed → free-gift line removed
   */
  const planSync = (cart, prevState = {}) => {
    const parentQtyMap = getParentQuantityMap(cart);
    const prevParents = prevState.parents || {};
    const prevGifts = prevState.gifts || {};
    const updates = {};
    const paidAdds = [];

    (cart.items || []).forEach((item) => {
      if (!isFreeGift(item)) return;

      const parentId = String(getProperty(item, FREE_GIFT_PARENT_KEY) || "");
      const parentQty = parentId ? parentQtyMap[parentId] || 0 : 0;

      // No machine → remove free gift
      if (parentQty <= 0) {
        if (item.quantity !== 0) updates[item.key] = 0;
        return;
      }

      // Already in sync
      if (item.quantity === parentQty) return;

      // Always set free-gift qty to machine qty
      updates[item.key] = parentQty;

      // If accessory was increased above machine qty, charge the excess
      if (item.quantity > parentQty) {
        const prevGiftQty = prevGifts[item.key];
        const prevParentQty = prevParents[parentId];
        const giftIncreased = prevGiftQty != null && item.quantity > prevGiftQty;
        const machineDecreased = prevParentQty != null && parentQty < prevParentQty;

        if (giftIncreased && !machineDecreased) {
          queuePaidExcess(cart, updates, paidAdds, item.variant_id, item.quantity - parentQty);
        }
      }
    });

    return {
      updates,
      paidAdds,
      hasChanges: Object.keys(updates).length > 0 || paidAdds.length > 0
    };
  };

  const refreshCartUi = (cartContent) => {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart: cartContent }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart: cartContent }
      })
    );
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartUrl()}.js`, { credentials: "same-origin" });
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  };

  const postCartItems = async (items) => {
    const response = await fetch(`${cartUrl()}/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items,
        sections: isCartPage() ? undefined : "mini-cart"
      })
    });
    if (!response.ok) return null;
    return response.json();
  };

  const syncFreeGifts = async (cart) => {
    if (syncing || !cart || !Array.isArray(cart.items)) return;

    const hasParents = (cart.items || []).some((item) => !isFreeGift(item));
    let giftMap = {};

    if (hasParents) {
      try {
        giftMap = await fetchGiftMap();
      } catch (error) {
        console.error("Free gift map fetch failed:", error);
      }
    }

    const prevState = readState();
    const plan = planSync(cart, prevState);
    const giftAdds = planMissingGifts(cart, giftMap);
    const hasChanges = plan.hasChanges || giftAdds.length > 0;

    if (!hasChanges) {
      writeState(cart);
      return;
    }

    syncing = true;
    try {
      let latestCart = cart;

      if (Object.keys(plan.updates).length > 0) {
        const updateResponse = await fetch(`${cartUrl()}/update.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates: plan.updates,
            sections: isCartPage() ? undefined : "mini-cart"
          })
        });

        if (!updateResponse.ok) return;
        latestCart = await updateResponse.json();
      }

      // Backfill missing free gifts before converting excess to paid lines
      if (giftAdds.length > 0) {
        const giftAddJson = await postCartItems(giftAdds);
        if (!giftAddJson) return;
        latestCart = await fetchCart();
        latestCart.sections = giftAddJson.sections || latestCart.sections;
      }

      if (plan.paidAdds.length > 0) {
        const addJson = await postCartItems(plan.paidAdds);
        if (!addJson) return;

        if (isCartPage()) {
          latestCart = await fetchCart();
          writeState(latestCart);
          window.location.reload();
          return;
        }

        latestCart = await fetchCart();
        latestCart.sections = addJson.sections || latestCart.sections;
      } else if (giftAdds.length > 0 && isCartPage()) {
        writeState(latestCart);
        window.location.reload();
        return;
      } else if (isCartPage() && Object.keys(plan.updates).length > 0) {
        writeState(latestCart);
        window.location.reload();
        return;
      }

      writeState(latestCart);
      refreshCartUi(latestCart);
    } catch (error) {
      console.error("Free gift cart sync failed:", error);
    } finally {
      syncing = false;
    }
  };

  document.documentElement.addEventListener("cart:updated", (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) syncFreeGifts(cart);
  });

  const syncFromServer = async () => {
    try {
      const cart = await fetchCart();
      await syncFreeGifts(cart);
    } catch (error) {
      console.error("Free gift cart fetch failed:", error);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncFromServer);
  } else {
    syncFromServer();
  }
})();
